import React, { useState, useEffect, useRef } from 'react';
import { TextField, InputAdornment, Button, CircularProgress, Box, Typography, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formFieldStyles } from './FormField.styles';

// Sayı tipinde input + yandan "Otomatik Doldur" butonu. field.onFetch async:
//   (formData) => number | { amount, helperText?, severity?, panel? } | null
//   - number/string  : alan değerine yaz
//   - object         : amount → alan; helperText → input altı (sade) mesaj;
//                      severity → renk; panel → input ALTINDA ayrı bilgi kutusu
//                      (2+ satır, kural/x/y/ciro toplamı için).
const NumberWithButtonField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  formData,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [customInfo, setCustomInfo] = useState(null);
  const [panel, setPanel] = useState(null); // { lines: string[], severity: 'info'|'warning'|'error' }

  const handleChange = (event) => {
    onChange?.(field.name, event.target.value, event);
    // Opt-in: field.manualFlagField tanimliysa, her elle keystroke'ta
    // flag'i true'ya cek. "Otomatik Doldur" basariyla amount dondurdugu
    // anda handleFetch false'a ceker; buton basarisiz olursa (amount null)
    // false SET EDILMEZ — kullanici elle yazacagi icin manuel kalir.
    if (field.manualFlagField) {
      onChange?.(field.manualFlagField, true, null);
    }
  };

  const handleBlur = (event) => {
    onBlur?.(field.name, value, event);
  };

  const handleFetch = async () => {
    if (typeof field.onFetch !== 'function') return;
    setBusy(true);
    try {
      // Flag SADECE amount basariyla dondugunde false'a cekilir. Buton
      // bastirilip sonuc null gelirse kullanici elle yazacak — o
      // durumda handleChange zaten flag'i true'ya alir; manuel chip
      // dogru sekilde gorunur.
      const result = await field.onFetch(formData || {});
      if (result == null) {
        setCustomInfo(null);
        setPanel(null);
        return;
      }
      if (typeof result === 'number' || typeof result === 'string') {
        if (!Number.isNaN(Number(result))) {
          onChange?.(field.name, String(result), null);
          if (field.manualFlagField) onChange?.(field.manualFlagField, false, null);
        }
        setCustomInfo(null);
        setPanel(null);
        return;
      }
      if (typeof result === 'object') {
        if (result.amount != null && !Number.isNaN(Number(result.amount))) {
          onChange?.(field.name, String(result.amount), null);
          if (field.manualFlagField) onChange?.(field.manualFlagField, false, null);
        }
        // extraFields: { field_name: value, ... } — Otomatik Doldur ile diger
        // form alanlarini da (ay_sayisi, toplam_ay gibi) tek seferde doldur.
        if (result.extraFields && typeof result.extraFields === 'object') {
          for (const [k, v] of Object.entries(result.extraFields)) {
            if (v == null) continue;
            onChange?.(k, v, null);
          }
        }
        setCustomInfo({
          text: result.helperText || null,
          severity: result.severity || 'info',
        });
        if (result.panel && Array.isArray(result.panel.lines) && result.panel.lines.length > 0) {
          setPanel({ lines: result.panel.lines, severity: result.panel.severity || result.severity || 'info' });
        } else {
          setPanel(null);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // Otomatik tetikleyici. field.autoFetchKey(formData) bir string doner —
  // key her degistiginde handleFetch calisir.
  // Not: autoFetchKey formData disi kaynaklara da bakabilir (parent'in
  // ref'leri). Bu durumda useEffect deps'inde sadece formData olmasi eksik
  // kalir — parent tarafta field taniminda `autoFetchExtraDep(formData)`
  // fonksiyonu tanimlanmalidir; bu fonksiyonun sonucu deps'e girer, degeri
  // degisince fetch yeniden tetiklenir.
  const lastAutoKeyRef = useRef(null);
  const extraDepValue = typeof field.autoFetchExtraDep === 'function'
    ? field.autoFetchExtraDep(formData || {})
    : null;
  useEffect(() => {
    if (typeof field.autoFetchKey !== 'function') return;
    const key = field.autoFetchKey(formData || {});
    if (!key) {
      lastAutoKeyRef.current = null;
      return;
    }
    if (lastAutoKeyRef.current === key) return;
    lastAutoKeyRef.current = key;
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, extraDepValue]);

  const buttonLabel = field.fetchButtonLabel
    ? t(field.fetchButtonLabel, field.fetchButtonLabel)
    : t('common.fetch', 'Getir');

  const helperTextValue = customInfo?.text || error || field.helperText || '';
  const helperColor = (() => {
    if (error) return undefined;
    if (!customInfo?.text) return undefined;
    if (customInfo.severity === 'warning') return 'warning.main';
    if (customInfo.severity === 'error') return 'error.main';
    return 'info.main';
  })();

  // Warning/error panelini MUI Alert olarak render ediyoruz — ikon + belirgin
  // arkaplan + baslik. Info panellerde eski sade Box gorunumunu koruyoruz
  // (kural/x/y ozeti gibi bilgilendirme).
  const panelIsAlert = panel?.severity === 'warning' || panel?.severity === 'error';
  const panelBg = panel?.severity === 'warning'
    ? 'warning.light'
    : panel?.severity === 'error'
    ? 'error.light'
    : 'action.hover';
  const panelBorder = panel?.severity === 'warning'
    ? 'warning.main'
    : panel?.severity === 'error'
    ? 'error.main'
    : 'divider';
  // Alert basligi: warning icin "Manuel giriş gerekiyor", error icin
  // "Işlem yapılamaz". Panel line'larindaki ilk cumle basliktan sonra
  // aciklama olarak gorunur.
  const alertTitle = panel?.severity === 'warning'
    ? t('common.manualEntryRequired', 'Manuel giriş gerekiyor')
    : panel?.severity === 'error'
    ? t('common.actionBlocked', 'İşlem yapılamaz')
    : null;

  return (
    <Box sx={{ width: '100%' }}>
    <TextField
      disabled={disabled}
      error={!!error || customInfo?.severity === 'error'}
      fullWidth
      sx={{
        ...formFieldStyles.field,
        ...(helperColor
          ? { '& .MuiFormHelperText-root': { color: helperColor, fontWeight: 600 } }
          : {}),
      }}
      type="number"
      label={field.label ? t(field.label) : ''}
      placeholder={field.placeholder ? t(field.placeholder) : ''}
      value={value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
      helperText={helperTextValue}
      InputProps={{
        endAdornment: field.hideButton ? null : (
          <InputAdornment position="end">
            <Button
              size="small"
              variant="outlined"
              onClick={handleFetch}
              disabled={disabled || busy}
              startIcon={busy ? <CircularProgress size={14} /> : null}
            >
              {buttonLabel}
            </Button>
          </InputAdornment>
        ),
      }}
    />
    {panel && panelIsAlert && (
      <Alert
        severity={panel.severity}
        variant="outlined"
        sx={{
          mt: 1,
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>{alertTitle}</AlertTitle>
        {panel.lines.map((line, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              lineHeight: 1.5,
              mb: i < panel.lines.length - 1 ? 0.5 : 0,
            }}
          >
            {line}
          </Typography>
        ))}
      </Alert>
    )}
    {panel && !panelIsAlert && (
      <Box
        sx={{
          mt: 1,
          p: 1.25,
          borderRadius: 1,
          border: 1,
          borderColor: panelBorder,
          bgcolor: panelBg,
        }}
      >
        {panel.lines.map((line, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={{
              fontSize: '0.82rem',
              lineHeight: 1.5,
              color: 'text.primary',
              mb: i < panel.lines.length - 1 ? 0.5 : 0,
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    )}
    </Box>
  );
};

export default NumberWithButtonField;
